import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Form, Input, Modal } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePostponeCommission } from "../../../../../common/api/queries";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import { ModalSizes } from "../../../../../common/constants";
import type { UUID } from "../../../../../typings/global";
import { Commission, PostponeCommissions } from "../../types";

interface Props {
  commissions?: Commission[];
  batchId: UUID;
  open: boolean;
  onFormCancel: () => void;
}

export const CommissionPostponeForm = ({ commissions, batchId, open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<PostponeCommissions>();

  const postponeCommission = usePostponeCommission();

  useEffect(() => {
    if (open && commissions?.length) {
      form.setFieldsValue({
        entries: commissions.map((commission) => ({
          id: commission.id,
          optimisticLockVersion: commission.optimisticLockVersion,
        })),
      });
    }
  }, [open, commissions, form]);

  const handleFormSubmit = () => {
    if (commissions?.length) {
      form
        .validateFields()
        .then((values) =>
          postponeCommission.mutate(
            { id: batchId, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "commissions.batch.attrs.commission", error.violations),
            },
          ),
        )
        .catch(resolveFormValidationError);
    }
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={
        commissions && commissions.length === 1
          ? t("commissions.batch.titles.postponeCommission")
          : t("commissions.batch.titles.postponeCommissions")
      }
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={postponeCommission.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="commissionPostponeForm">
        <HiddenInput name="entries" />

        <Form.Item
          name="manualPostponementReason"
          label={t("commissions.batch.attrs.commission.manualPostponementReason")}
          rules={[validations.notBlank, validations.size(1, 255)]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};
